import type { Log, PresentationTaskRunner, WinHighlightEffect } from "@slot/presentation";
import type { AudioService } from "../../audio/ports/AudioService";
import type { AudioEventId } from "../../audio/ports/types";
import type { HapticsService } from "../../haptics/HapticsService";
import { multiplierBreakdown } from "./multiplierBreakdown";
import { WinCyclePresenter, type WinCycleTuning } from "./WinCyclePresenter";

/**
 * A title's win meter, when it has one: the readout the tumble's win label flies into at `update_total_win_meter`
 * (slot-cannon: the plank above the grid). The shell's current-win readout in the bar is separate and always there.
 */
export interface WinMeterPort {
  /** A positive total first makes way on the meter (slot-cannon: the plank's idle tip). */
  clearTip?(): void;
  /** Counts to the server's running total in `scope`, the collect window a slam finishes. */
  presentTotalWinMinor(totalMinor: string, scope: string): Promise<void>;
  /** A new spin, or a feature exit: the meter hides and resets. */
  hideForNewSpin(): void;
}

/** The win sequence's own timing (a title's values). */
export interface WinSequenceTuning {
  /** The beat before a win: the spin's first win waits `firstSeconds` (after the reels land), each next one `subsequentSeconds`. */
  winHighlightDelay: { firstSeconds: number; subsequentSeconds: number };
}

/**
 * Base of the per-win scopes (`win_present_0`, `win_present_1`, …). Each win paces itself under its own scope, so a slam
 * latches only the win presenting ({@link WinSequencePresenter.slamActiveWin}): it snaps to fully shown and the
 * sequence moves on, while later wins keep fresh scopes and play until slammed too.
 */
const WIN_PRESENTATION_SCOPE_PREFIX = "win_present";

/**
 * The scope of one `update_total_win_meter`: the win label flying into the meter and the meter's count after it. A slam
 * latches it ({@link WinSequencePresenter.slamWinCollect}): the label lands and the meter shows the server's total at
 * once. The window closes with the update.
 */
const WIN_COLLECT_SCOPE = "win_collect";

/**
 * The round's win sequence, the same in every title (decision 46; slot-cannon's until then): each `grid_win_highlight`
 * after its beat, with the win cue (a title's ladder of cues, decision 61) and a soft haptic, the amount label showing
 * "base × multiplier" when the server applied one and the breakdown is exact (decision 60); a slam snaps only the win
 * presenting; with a `cycle`, the spin's wins re-shown line by line through idle until the next spin (decision 65);
 * `update_total_win_meter` collects the labels into the title's meter in a window a slam finishes; the scatter collect
 * lights its cells over the win dim. It presents what the engine tells it and computes no math. A title's features hook
 * in around it: slot-cannon's cannon merges the deferred labels on its shot's impact (`mergeActiveWinLabels`) and
 * resets its fire-sound ladder each spin.
 */
export class WinSequencePresenter {
  private readonly runner: PresentationTaskRunner;
  private readonly audioService: AudioService;
  private readonly hapticsService: HapticsService;
  private readonly winHighlightEffect: WinHighlightEffect;
  private readonly winMeter: WinMeterPort | null;
  private readonly tuning: WinSequenceTuning;
  private readonly deferMultiplierMerge: boolean;
  private readonly showMultiplierBreakdown: boolean;
  private readonly winCueEvents: readonly AudioEventId[];
  private readonly winCycle: WinCyclePresenter | null;
  /** This spin's win index (0-based): the first win takes the first-win beat; the per-win scope. Reset every spin. */
  private winCounter = 0;
  /** The scope of the win presenting now (null between wins): what a slam targets. */
  private activeWinScope: string | null = null;
  /** True while a meter update's collect window is open — what `slamWinCollect` reaches. */
  private collectWindowOpen = false;

  public constructor(input: {
    runner: PresentationTaskRunner;
    audioService: AudioService;
    hapticsService: HapticsService;
    winHighlightEffect: WinHighlightEffect;
    /** The title's meter; null → only the shell's current-win readout shows the total. */
    winMeter?: WinMeterPort | null;
    tuning: WinSequenceTuning;
    /**
     * Hold a multiplied win's label at its base until `mergeActiveWinLabels` (slot-cannon: the cannon's impact). Off:
     * the label shows "base × multiplier → total" by itself.
     */
    deferMultiplierMerge?: boolean;
    /**
     * Whether a multiplied win's label shows "base × multiplier → total" (default true; only ever when exact). Off:
     * the label shows the win's amount (slot-bloody-teddy: its breakdowns are its idle cycle's, line by line).
     */
    showMultiplierBreakdown?: boolean;
    /**
     * The cue each win emits, by its index in the spin, wrapping: win 1 the first, win 2 the second, and past the last
     * back to the first; each spin starts again at the first (slot-bloody-teddy: three ids its mapping gives three
     * pitches of one sound). Default: `grid.win_highlight` for every win.
     */
    winCueEvents?: readonly AudioEventId[];
    /**
     * The idle cycle (decision 65): the spin's wins re-shown line by line from the spin's end until the next spin or a
     * feature's exit. Absent: none. A title whose pre-spin runs before the round's first boundary stops it there
     * (`stopWinCycle`); every spin boundary stops it too.
     */
    cycle?: WinCycleTuning;
    log?: Log;
  }) {
    this.runner = input.runner;
    this.audioService = input.audioService;
    this.hapticsService = input.hapticsService;
    this.winHighlightEffect = input.winHighlightEffect;
    this.winMeter = input.winMeter ?? null;
    this.tuning = input.tuning;
    this.deferMultiplierMerge = input.deferMultiplierMerge ?? false;
    this.showMultiplierBreakdown = input.showMultiplierBreakdown ?? true;
    this.winCueEvents = input.winCueEvents && input.winCueEvents.length > 0 ? input.winCueEvents : ["grid.win_highlight"];
    this.winCycle = input.cycle ? new WinCyclePresenter({ highlight: input.winHighlightEffect, runner: input.runner, tuning: input.cycle, log: input.log ?? (() => {}) }) : null;
  }

  /**
   * A new spin: the next win takes the first-win beat again, and the previous spin's win scopes are cleared, so a slam
   * in spin N cannot carry into spin N+1's reused scope ids and snap its win at once.
   */
  public resetWinHighlightSequence(): void {
    for (let i = 0; i < this.winCounter; i++) this.runner.clearScope(this.scopeForWin(i));
    this.activeWinScope = null;
    this.winCounter = 0;
  }

  /** Slams the win presenting now: it snaps to fully shown and the sequence moves on. False between wins. */
  public slamActiveWin(): boolean {
    if (!this.activeWinScope) return false;
    return this.runner.slamFinishScope(this.activeWinScope);
  }

  /** Slams the win flying into the meter: the label lands and the meter shows the total at once. False outside an update. */
  public slamWinCollect(): boolean {
    if (!this.collectWindowOpen) return false;
    return this.runner.slamFinishScope(WIN_COLLECT_SCOPE);
  }

  /**
   * `grid_win_highlight`: the beat, the cue and haptic, then the dim, the winners' effect and the amount label. The
   * group's member lines go to the highlight with it (a payline stroke draws each of them; the union drives the rest).
   */
  public async presentGridWinHighlight(input: {
    positionMask: string;
    winAmount?: string;
    /** The win group's member lines (≥1); `positionMask` is their union, presented as one highlight. */
    lines?: Array<{ positionMask: string; winAmount?: string; symbolCount?: string; baseWinAmount?: string; multiplierApplied?: string }>;
  }): Promise<void> {
    const scope = this.scopeForWin(this.winCounter);
    this.activeWinScope = scope;
    this.winCycle?.record(input);
    try {
      const { firstSeconds, subsequentSeconds } = this.tuning.winHighlightDelay;
      const delaySeconds = this.winCounter === 0 ? firstSeconds : subsequentSeconds;
      if (delaySeconds > 0) await this.runner.sleep(delaySeconds, scope);
      const cue = this.winCueEvents[this.winCounter % this.winCueEvents.length]!;
      this.winCounter++;
      this.audioService.presentation.emit({ id: cue });
      this.hapticsService.play("soft");
      const labelBreakdown = this.showMultiplierBreakdown ? multiplierBreakdown(input.lines, input.winAmount) : undefined;
      const lineMasks = input.lines && input.lines.length > 0 ? input.lines.map((line) => line.positionMask) : undefined;
      await this.winHighlightEffect.highlightWinByMask(
        input.positionMask,
        input.winAmount,
        scope,
        lineMasks,
        labelBreakdown,
        labelBreakdown && this.deferMultiplierMerge ? { deferBreakdownMerge: true } : undefined,
      );
    } finally {
      if (this.activeWinScope === scope) this.activeWinScope = null;
    }
  }

  /** The spin's wins start their idle re-show (the shell calls it at the spin's end). No-op without a cycle or wins. */
  public startWinCycle(): void {
    this.winCycle?.start();
  }

  /** The idle re-show stops and forgets the spin's wins (a spin's start, a feature's exit). */
  public stopWinCycle(): void {
    this.winCycle?.stop();
  }

  /** The deferred labels morph "base" → "base × multiplier" → total (a title's multiplier feature calls it). */
  public mergeActiveWinLabels(): void {
    this.winHighlightEffect.mergeActiveWinLabels();
  }

  /** Lights cells the win dimmed as non-winners (the scatter collect over a win). No-op with nothing dimmed. */
  public undimCellsAtIndices(indices: number[]): void {
    this.winHighlightEffect.undimCellsAtIndices(indices);
  }

  /**
   * `update_total_win_meter`: a positive total first makes way on the meter; then, in the collect window a slam
   * finishes, the win labels fly into the meter (the landing cue when one did) and the meter takes the server's total.
   */
  public async presentTotalWinAmount(totalWin: string): Promise<void> {
    if (BigInt(totalWin) > 0n) this.winMeter?.clearTip?.();
    await this.runner.runInScope(WIN_COLLECT_SCOPE, async () => {
      this.collectWindowOpen = true;
      try {
        if (await this.winHighlightEffect.collectActiveLabels(WIN_COLLECT_SCOPE)) {
          this.audioService.presentation.emit({ id: "grid.win_collect" });
        }
        await this.winMeter?.presentTotalWinMinor(totalWin, WIN_COLLECT_SCOPE);
      } finally {
        this.collectWindowOpen = false;
      }
    });
  }

  /** A new spin or a feature exit: the meter hides and resets. No-op without a meter. */
  public hideWinMeterForNewSpin(): void {
    this.winMeter?.hideForNewSpin();
  }

  private scopeForWin(index: number): string {
    return `${WIN_PRESENTATION_SCOPE_PREFIX}_${index}`;
  }
}
